import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import {
  BookOpen, Upload, Download, Trash2, Pencil, Search, Filter, Lock, Users,
  FileText, FileImage, FileVideo, FileArchive, File, Building2, Check, X,
  ShieldAlert, Plus, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import StickyPageHeader from "../components/StickyPageHeader";

const ROLE_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "gestionnaire", label: "Gestionnaire" },
  { value: "collaborateur", label: "Collaborateur" },
];

// Correspondance mime/ext → icône lucide pour la vignette du document.
function fileIcon(mime, ext) {
  const m = (mime || "").toLowerCase();
  const e = (ext || "").toLowerCase();
  if (m.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(e))
    return { Icon: FileImage, tint: "#0EA5E9", label: "Image" };
  if (m.startsWith("video/") || ["mp4", "mov", "webm"].includes(e))
    return { Icon: FileVideo, tint: "#DC2626", label: "Vidéo" };
  if (m === "application/pdf" || e === "pdf")
    return { Icon: FileText, tint: "#EF4444", label: "PDF" };
  if (m.includes("word") || ["doc", "docx", "rtf"].includes(e))
    return { Icon: FileText, tint: "#2563EB", label: "Word" };
  if (m.includes("excel") || m.includes("sheet") || ["xls", "xlsx", "csv"].includes(e))
    return { Icon: FileText, tint: "#059669", label: "Tableur" };
  if (m.includes("presentation") || ["ppt", "pptx"].includes(e))
    return { Icon: FileText, tint: "#F97316", label: "Slides" };
  if (m === "application/zip" || e === "zip")
    return { Icon: FileArchive, tint: "#6B7280", label: "Archive" };
  return { Icon: File, tint: "#71717A", label: "Fichier" };
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

/** Combobox multi-sélection pour brands (départements). */
function MultiBrandPicker({ value, onChange, brands, testid, disabled }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between font-normal"
          data-testid={testid}
          disabled={disabled}
        >
          <span className="truncate">
            {value.length === 0 ? "Tous les départements" : `${value.length} département(s) sélectionné(s)`}
          </span>
          <Filter size={14} className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher un département…" />
          <CommandList>
            <CommandEmpty>Aucun département.</CommandEmpty>
            <CommandGroup>
              {brands.map((b) => {
                const checked = value.includes(b.id);
                return (
                  <CommandItem key={b.id} onSelect={() => toggle(b.id)} data-testid={`brand-pick-${b.id}`}>
                    <span className="h-3 w-3 rounded-full mr-2" style={{ backgroundColor: b.color }} />
                    <span className="flex-1 truncate">{b.name}</span>
                    {checked && <Check size={14} className="text-emerald-600" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Combobox de sélection de collaborateurs individuels (multi). */
function MultiUserPicker({ value, onChange, users, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((x) => x !== id) : [...value, id];
    onChange(next);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal" data-testid={testid}>
          <span className="truncate">
            {value.length === 0 ? "Aucun collaborateur exclu" : `${value.length} collaborateur(s) exclu(s)`}
          </span>
          <Users size={14} className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher un collaborateur…" />
          <CommandList>
            <CommandEmpty>Aucun résultat.</CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-auto">
              {users.map((u) => {
                const checked = value.includes(u.id);
                return (
                  <CommandItem key={u.id} onSelect={() => toggle(u.id)} data-testid={`user-pick-${u.id}`}>
                    <span className="flex-1 truncate">
                      <span className="font-medium">{u.name || u.email}</span>
                      <span className="text-[10px] text-zinc-400 ml-1 font-mono">{u.role}</span>
                    </span>
                    {checked && <Check size={14} className="text-emerald-600" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Détermine le type de rendu inline supporté par le navigateur pour un document donné. */
function previewKind(mime, ext) {
  const m = (mime || "").toLowerCase();
  const e = (ext || "").toLowerCase();
  if (m === "application/pdf" || e === "pdf") return "pdf";
  if (m.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp", "avif"].includes(e)) return "image";
  if (m.startsWith("video/") || ["mp4", "webm", "mov", "m4v"].includes(e)) return "video";
  if (m.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "flac", "aac"].includes(e)) return "audio";
  if (m.startsWith("text/") || ["txt", "md", "csv", "json", "xml", "html", "log", "js", "py", "css"].includes(e)) return "text";
  return "unsupported";
}

const emptyForm = {
  file: null,
  name: "",
  description: "",
  category: "",
  brand_ids: [],
  is_confidential: false,
  restricted_user_ids: [],
  restricted_roles: [],
};

export default function BibliothequeDocuments() {
  const { user } = useAuth();
  const { brands } = useBrands();

  const canUpload = ["admin", "manager", "gestionnaire"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const canConfidential = user?.role === "admin";

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categories, setCategories] = useState([]);

  // Formulaires
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [users, setUsers] = useState([]);

  // Preview (icône œil)
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (categoryFilter) params.category = categoryFilter;
      if (brandFilter) params.brand_id = brandFilter;
      const { data } = await api.get("/library/documents", { params });
      setDocs(data);
    } catch (e) {
      toast.error("Impossible de charger la bibliothèque");
    } finally {
      setLoading(false);
    }
  }, [q, categoryFilter, brandFilter]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/library/documents/categories");
      setCategories(data);
    } catch { /* silent */ }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!canConfidential) return;
    try {
      const { data } = await api.get("/collaborateurs");
      setUsers(Array.isArray(data) ? data : (data.items || []));
    } catch { /* silent */ }
  }, [canConfidential]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Débounce simple pour la recherche
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (doc) => {
    setEditingId(doc.id);
    setForm({
      file: null,
      name: doc.name || "",
      description: doc.description || "",
      category: doc.category || "",
      brand_ids: doc.brand_ids || [],
      is_confidential: !!doc.is_confidential,
      restricted_user_ids: doc.restricted_user_ids || [],
      restricted_roles: doc.restricted_roles || [],
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!editingId && !form.file) {
      toast.error("Sélectionnez un fichier à envoyer");
      return;
    }
    if (!form.name.trim() && !editingId) {
      toast.error("Le nom du document est obligatoire");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const payload = {
          name: form.name,
          description: form.description,
          category: form.category,
          brand_ids: form.brand_ids,
        };
        if (canConfidential) {
          payload.is_confidential = form.is_confidential;
          payload.restricted_user_ids = form.restricted_user_ids;
          payload.restricted_roles = form.restricted_roles;
        }
        await api.patch(`/library/documents/${editingId}`, payload);
        toast.success("Document mis à jour");
      } else {
        const fd = new FormData();
        fd.append("file", form.file);
        fd.append("name", form.name || form.file.name);
        fd.append("description", form.description);
        fd.append("category", form.category);
        fd.append("brand_ids", form.brand_ids.join(","));
        fd.append("is_confidential", form.is_confidential ? "true" : "false");
        fd.append("restricted_user_ids", form.restricted_user_ids.join(","));
        fd.append("restricted_roles", form.restricted_roles.join(","));
        await api.post("/library/documents", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Document ajouté à la bibliothèque");
      }
      setDialogOpen(false);
      setForm(emptyForm);
      load();
      loadCategories();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec de l'enregistrement";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const doDownload = async (doc) => {
    try {
      const res = await api.get(`/library/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.mime_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 403 ? "Accès restreint" : "Téléchargement impossible";
      toast.error(msg);
    }
  };

  const openPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewError(null);
    setPreviewText("");
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/library/documents/${doc.id}/preview`, { responseType: "blob" });
      const mime = doc.mime_type || res.headers?.["content-type"] || "application/octet-stream";
      const ext = (doc.original_filename || "").split(".").pop();
      const kind = previewKind(mime, ext);
      const blob = new Blob([res.data], { type: mime });
      if (kind === "text") {
        const txt = await blob.text();
        setPreviewText(txt.slice(0, 200_000)); // safeguard 200 Ko affichés
      }
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (e) {
      const status = e?.response?.status;
      const msg = status === 403 ? "Accès restreint à ce document" : "Impossible de charger l'aperçu";
      setPreviewError(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
    }
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewText("");
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const doDelete = async (doc) => {
    if (!window.confirm(`Supprimer définitivement « ${doc.name} » ?`)) return;
    try {
      await api.delete(`/library/documents/${doc.id}`);
      toast.success("Document supprimé");
      load();
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const brandLabel = useCallback((id) => brands.find((b) => b.id === id)?.name || id, [brands]);
  const brandColor = useCallback((id) => brands.find((b) => b.id === id)?.color || "#71717A", [brands]);

  const filteredDocs = useMemo(() => docs, [docs]);

  return (
    <div className="space-y-4" data-testid="library-page">
      <StickyPageHeader testid="library-sticky-header">
        {/* Header : titre + bouton d'ajout */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="label-uppercase mb-2">Documents d'entreprise</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900 flex items-center gap-3">
              <BookOpen size={28} strokeWidth={1.5} className="text-purple-600" />
              Bibliothèque de documents
            </h1>
            <p className="text-sm text-zinc-600 mt-1 font-mono">
              Documents partagés d'entreprise · accès contrôlé par département et confidentialité
            </p>
          </div>
          {canUpload && (
            <Button onClick={openCreate} className="bg-purple-600 hover:bg-purple-700" data-testid="library-add-btn">
              <Upload size={14} className="mr-1.5" /> Ajouter un document
            </Button>
          )}
        </div>

        {/* Barre de filtres — responsive : empilée sur mobile, en ligne sur desktop */}
        <div className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-col sm:flex-row gap-2" data-testid="library-filters">
        <div className="relative flex-1 min-w-0">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Rechercher un document, une description ou une catégorie…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
            data-testid="library-search"
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between font-normal min-w-[180px]" data-testid="library-filter-category">
              <span className="truncate">{categoryFilter || "Toutes catégories"}</span>
              <Filter size={14} className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Rechercher une catégorie…" />
              <CommandList>
                <CommandEmpty>Aucune catégorie.</CommandEmpty>
                <CommandGroup>
                  <CommandItem onSelect={() => setCategoryFilter("")}>
                    <span className="flex-1">Toutes catégories</span>
                    {!categoryFilter && <Check size={14} className="text-emerald-600" />}
                  </CommandItem>
                  {categories.map((c) => (
                    <CommandItem key={c} onSelect={() => setCategoryFilter(c)} data-testid={`library-cat-${c}`}>
                      <span className="flex-1 truncate">{c}</span>
                      {categoryFilter === c && <Check size={14} className="text-emerald-600" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between font-normal min-w-[200px]" data-testid="library-filter-brand">
              <span className="truncate">{brandFilter ? brandLabel(brandFilter) : "Tous départements"}</span>
              <Building2 size={14} className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Rechercher un département…" />
              <CommandList>
                <CommandEmpty>Aucun département.</CommandEmpty>
                <CommandGroup>
                  <CommandItem onSelect={() => setBrandFilter("")}>
                    <span className="flex-1">Tous départements</span>
                    {!brandFilter && <Check size={14} className="text-emerald-600" />}
                  </CommandItem>
                  {brands.map((b) => (
                    <CommandItem key={b.id} onSelect={() => setBrandFilter(b.id)} data-testid={`library-brand-${b.id}`}>
                      <span className="h-3 w-3 rounded-full mr-2" style={{ backgroundColor: b.color }} />
                      <span className="flex-1 truncate">{b.name}</span>
                      {brandFilter === b.id && <Check size={14} className="text-emerald-600" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      </StickyPageHeader>

      {/* Grille documents */}
      {loading ? (
        <div className="py-12 text-center text-sm text-zinc-500 font-mono" data-testid="library-loading">Chargement…</div>
      ) : filteredDocs.length === 0 ? (
        <div className="py-12 text-center text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm" data-testid="library-empty">
          <BookOpen className="mx-auto mb-2 text-zinc-300" size={32} />
          Aucun document ne correspond aux filtres.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="library-grid">
          {filteredDocs.map((doc) => {
            const ext = (doc.original_filename || "").split(".").pop();
            const { Icon, tint, label } = fileIcon(doc.mime_type, ext);
            const canEditDoc = canUpload && (user?.role === "admin" || doc.uploaded_by === user?.id);
            return (
              <div
                key={doc.id}
                className="bg-white border border-zinc-200 rounded-sm p-3 flex flex-col gap-2 hover:border-purple-300 transition-colors"
                data-testid={`library-card-${doc.id}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="h-12 w-12 rounded-sm flex flex-col items-center justify-center flex-shrink-0 border"
                    style={{ backgroundColor: `${tint}10`, borderColor: `${tint}30`, color: tint }}
                  >
                    <Icon size={20} />
                    <span className="text-[8px] font-mono uppercase mt-0.5">{label}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 leading-tight line-clamp-2" title={doc.name} data-testid={`library-name-${doc.id}`}>
                      {doc.name}
                    </p>
                    <p className="text-[10px] text-zinc-400 font-mono mt-0.5 truncate" title={doc.original_filename}>
                      {doc.original_filename} · {formatBytes(doc.size_bytes)}
                    </p>
                  </div>
                  {doc.is_confidential && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-red-50 border border-red-200 text-red-700 flex-shrink-0"
                      title="Document confidentiel"
                      data-testid={`library-confidential-${doc.id}`}
                    >
                      <Lock size={9} /> Conf.
                    </span>
                  )}
                </div>

                {doc.description && (
                  <p className="text-xs text-zinc-600 line-clamp-2" title={doc.description}>{doc.description}</p>
                )}

                <div className="flex flex-wrap items-center gap-1">
                  {doc.category && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-200" data-testid={`library-cat-tag-${doc.id}`}>
                      {doc.category}
                    </span>
                  )}
                  {(doc.brand_ids || []).length === 0 ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-50 text-zinc-500 border border-zinc-200">Tous départements</span>
                  ) : (
                    (doc.brand_ids || []).slice(0, 3).map((bid) => (
                      <span key={bid} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border" style={{ backgroundColor: `${brandColor(bid)}15`, borderColor: `${brandColor(bid)}40`, color: brandColor(bid) }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: brandColor(bid) }} />
                        {brandLabel(bid)}
                      </span>
                    ))
                  )}
                  {(doc.brand_ids || []).length > 3 && (
                    <span className="text-[10px] font-mono text-zinc-500">+{(doc.brand_ids || []).length - 3}</span>
                  )}
                </div>

                <div className="flex items-center justify-between mt-1 pt-2 border-t border-zinc-100">
                  <div className="text-[10px] text-zinc-400 font-mono truncate flex-1 min-w-0" title={`Ajouté par ${doc.uploaded_by_name}`}>
                    {formatDate(doc.created_at)} · {doc.uploaded_by_name || "—"}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-purple-700 hover:bg-purple-50 hover:text-purple-800 border-purple-200" onClick={() => openPreview(doc)} data-testid={`library-preview-${doc.id}`} title="Visualiser">
                      <Eye size={12} />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => doDownload(doc)} data-testid={`library-download-${doc.id}`} title="Télécharger">
                      <Download size={12} />
                    </Button>
                    {canEditDoc && (
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openEdit(doc)} data-testid={`library-edit-${doc.id}`} title="Modifier">
                        <Pencil size={12} />
                      </Button>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => doDelete(doc)} data-testid={`library-delete-${doc.id}`} title="Supprimer">
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog visualisation (icône œil) */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col overflow-hidden"
          data-testid="library-preview-dialog"
        >
          <DialogHeader className="p-4 pb-3 border-b border-zinc-200 shrink-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <DialogTitle className="flex items-center gap-2 text-base font-display">
                  <Eye size={16} className="text-purple-600 shrink-0" />
                  <span className="truncate" data-testid="library-preview-title">{previewDoc?.name || "Aperçu du document"}</span>
                </DialogTitle>
                <DialogDescription className="text-[11px] font-mono text-zinc-500 mt-0.5 truncate">
                  {previewDoc?.original_filename} · {formatBytes(previewDoc?.size_bytes)}
                  {previewDoc?.mime_type ? ` · ${previewDoc.mime_type}` : ""}
                </DialogDescription>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {previewUrl && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                    data-testid="library-preview-newtab"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <ExternalLink size={12} className="mr-1" />
                    <span className="hidden sm:inline">Nouvel onglet</span>
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => previewDoc && doDownload(previewDoc)}
                  data-testid="library-preview-download"
                >
                  <Download size={12} className="mr-1" />
                  <span className="hidden sm:inline">Télécharger</span>
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto bg-zinc-50" data-testid="library-preview-body">
            {previewLoading && (
              <div className="h-full flex items-center justify-center text-sm text-zinc-500 font-mono" data-testid="library-preview-loading">
                Chargement de l'aperçu…
              </div>
            )}
            {previewError && !previewLoading && (
              <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="library-preview-error">
                <ShieldAlert size={36} className="text-red-500" strokeWidth={1.5} />
                <p className="text-sm text-red-700 font-medium">{previewError}</p>
                <p className="text-xs text-zinc-500 font-mono">Vous pouvez tout de même tenter le téléchargement si vous y avez accès.</p>
              </div>
            )}
            {!previewLoading && !previewError && previewDoc && previewUrl && (() => {
              const ext = (previewDoc.original_filename || "").split(".").pop();
              const kind = previewKind(previewDoc.mime_type, ext);
              if (kind === "pdf") {
                return (
                  <iframe
                    src={previewUrl}
                    title={previewDoc.name}
                    className="w-full h-full border-0 bg-white"
                    data-testid="library-preview-iframe-pdf"
                  />
                );
              }
              if (kind === "image") {
                return (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={previewUrl}
                      alt={previewDoc.name}
                      className="max-w-full max-h-full object-contain shadow-sm border border-zinc-200 bg-white"
                      data-testid="library-preview-image"
                    />
                  </div>
                );
              }
              if (kind === "video") {
                return (
                  <div className="w-full h-full flex items-center justify-center p-4 bg-black">
                    <video
                      src={previewUrl}
                      controls
                      className="max-w-full max-h-full"
                      data-testid="library-preview-video"
                    >
                      Votre navigateur ne supporte pas la lecture vidéo.
                    </video>
                  </div>
                );
              }
              if (kind === "audio") {
                return (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-6">
                    <div className="h-24 w-24 rounded-full bg-purple-100 border border-purple-200 flex items-center justify-center">
                      <FileText size={40} className="text-purple-600" strokeWidth={1.5} />
                    </div>
                    <p className="text-sm font-medium text-zinc-700">{previewDoc.name}</p>
                    <audio
                      src={previewUrl}
                      controls
                      className="w-full max-w-md"
                      data-testid="library-preview-audio"
                    >
                      Votre navigateur ne supporte pas la lecture audio.
                    </audio>
                  </div>
                );
              }
              if (kind === "text") {
                return (
                  <pre
                    className="p-4 text-xs font-mono whitespace-pre-wrap break-words text-zinc-800 bg-white h-full overflow-auto"
                    data-testid="library-preview-text"
                  >
                    {previewText || "(fichier vide)"}
                  </pre>
                );
              }
              // Format non prévisualisable → invitation à télécharger
              return (
                <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="library-preview-unsupported">
                  <File size={40} className="text-zinc-400" strokeWidth={1.5} />
                  <p className="text-sm text-zinc-700 font-medium">
                    Aperçu non disponible pour ce type de fichier
                  </p>
                  <p className="text-xs text-zinc-500 font-mono max-w-md">
                    Les fichiers Office (Word, Excel, PowerPoint), archives ZIP et autres formats propriétaires ne peuvent pas être affichés dans le navigateur. Téléchargez le document pour l'ouvrir avec l'application adaptée.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 mt-2"
                    onClick={() => doDownload(previewDoc)}
                    data-testid="library-preview-unsupported-download"
                  >
                    <Download size={12} className="mr-1" /> Télécharger le fichier
                  </Button>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog upload / édition */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="library-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Modifier le document" : "Ajouter un document"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Mettez à jour les informations. Le fichier lui-même ne peut pas être remplacé (créez un nouveau document si besoin)."
                : "Formats acceptés : PDF, Word, Excel, PowerPoint, images, vidéos, ZIP. Taille max 50 Mo."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {!editingId && (
              <div>
                <Label htmlFor="file">Fichier *</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null, name: f.name || e.target.files?.[0]?.name || "" }))}
                  data-testid="library-input-file"
                />
                {form.file && (
                  <p className="text-[10px] text-zinc-500 font-mono mt-1">
                    {form.file.name} · {formatBytes(form.file.size)}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex : Manuel qualité v2.pdf"
                data-testid="library-input-name"
              />
            </div>

            <div>
              <Label htmlFor="desc">Description</Label>
              <Textarea
                id="desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Description succincte, mots-clés utiles…"
                rows={2}
                data-testid="library-input-description"
              />
            </div>

            <div>
              <Label htmlFor="cat">Catégorie</Label>
              <Input
                id="cat"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="Ex : RH, Qualité, Formation…"
                list="library-categories-list"
                data-testid="library-input-category"
              />
              <datalist id="library-categories-list">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>

            <div>
              <Label>Départements autorisés</Label>
              <MultiBrandPicker
                value={form.brand_ids}
                onChange={(v) => setForm((f) => ({ ...f, brand_ids: v }))}
                brands={brands}
                testid="library-input-brands"
                disabled={user?.role === "manager"}
              />
              <p className="text-[10px] text-zinc-400 font-mono mt-0.5">
                Vide = accessible à tous les départements. {user?.role === "manager" && "Les managers publient dans leurs départements."}
              </p>
            </div>

            {canConfidential && (
              <div className="border border-red-200 bg-red-50 rounded-sm p-3 space-y-3" data-testid="library-confidential-block">
                <div className="flex items-center gap-2">
                  <ShieldAlert size={14} className="text-red-600" />
                  <Label htmlFor="conf" className="text-red-700 font-medium">Restrictions administrateur</Label>
                  <div className="ml-auto">
                    <Switch
                      id="conf"
                      checked={form.is_confidential}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, is_confidential: !!v }))}
                      data-testid="library-input-confidential"
                    />
                  </div>
                </div>
                {form.is_confidential && (
                  <>
                    <div>
                      <Label className="text-xs text-red-700">Rôles exclus</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ROLE_OPTIONS.map((r) => {
                          const active = form.restricted_roles.includes(r.value);
                          return (
                            <button
                              key={r.value}
                              type="button"
                              onClick={() => setForm((f) => ({
                                ...f,
                                restricted_roles: active
                                  ? f.restricted_roles.filter((x) => x !== r.value)
                                  : [...f.restricted_roles, r.value],
                              }))}
                              className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border transition-colors ${
                                active ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-red-400"
                              }`}
                              data-testid={`library-role-toggle-${r.value}`}
                            >
                              {r.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-red-700">Collaborateurs exclus (individuels)</Label>
                      <MultiUserPicker
                        value={form.restricted_user_ids}
                        onChange={(v) => setForm((f) => ({ ...f, restricted_user_ids: v }))}
                        users={users}
                        testid="library-input-restricted-users"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="library-cancel">
              <X size={14} className="mr-1" /> Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="library-submit"
            >
              {saving ? "Enregistrement…" : editingId ? "Mettre à jour" : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
